import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/Autocomplete";
import { listKontaktKunden, type Geschaeftspartner } from "@/lib/kontakte";
import {
  assignKundenGeschaeftspartner,
  listKundenGeschaeftspartner,
  unassignKundenGeschaeftspartner,
  type KundeOut,
  type KundenGeschaeftspartnerOut,
} from "../lib/settingsApi";

export interface KundenZuweisenDialogProps {
  open: boolean;
  onClose: () => void;
  kunde: KundeOut | null;
  onChanged?: () => void;
}

export function KundenZuweisenDialog({
  open,
  onClose,
  kunde,
  onChanged,
}: KundenZuweisenDialogProps) {
  const [items, setItems] = useState<KundenGeschaeftspartnerOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<AutocompleteOption<Geschaeftspartner> | null>(
    null,
  );

  const assignedIds = useMemo(() => new Set(items.map((i) => i.id)), [items]);

  const reload = useCallback(async () => {
    if (!kunde) return;
    setLoading(true);
    setError(null);
    try {
      setItems(await listKundenGeschaeftspartner(kunde.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunden konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [kunde]);

  useEffect(() => {
    if (open && kunde) {
      setSelected(null);
      void reload();
    }
  }, [open, kunde, reload]);

  const loadOptions = useCallback(
    async (query: string): Promise<AutocompleteOption<Geschaeftspartner>[]> => {
      const res = await listKontaktKunden({
        q: query.trim() || undefined,
        limit: 40,
        sort: "firmenname",
        direction: "asc",
      });
      return (res.items || [])
        .filter((gp) => !assignedIds.has(gp.id))
        .map((gp) => ({
          key: String(gp.id),
          label: gp.firmenname,
          description: [
            gp.kundennummer ? `Nr. ${gp.kundennummer}` : null,
            gp.ort || null,
            gp.kundencode != null ? `Code ${gp.kundencode}` : null,
          ]
            .filter(Boolean)
            .join(" · ") || undefined,
          value: gp,
        }));
    },
    [assignedIds],
  );

  async function handleAssign() {
    if (!kunde || !selected?.value) return;
    setSaving(true);
    setError(null);
    try {
      await assignKundenGeschaeftspartner(kunde.id, selected.value.id);
      setSelected(null);
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Zuweisen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleUnassign(gp: KundenGeschaeftspartnerOut) {
    if (!kunde) return;
    if (
      !window.confirm(
        `„${gp.firmenname}" von Code ${kunde.code} entfernen?`,
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await unassignKundenGeschaeftspartner(kunde.id, gp.id);
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Entfernen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  if (!kunde) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Kunden zuweisen – ${kunde.code} ${kunde.name}`}
      size="lg"
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      <p className="mb-3 text-sm text-label-secondary">
        App-Kunden diesem Kundencode zuweisen. Bei der Projektanlage erscheinen
        genau diese Firmen zur Auswahl.
      </p>

      {error && (
        <Alert variant="error" title="Fehler" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <LockedAutocomplete<Geschaeftspartner>
            label="Kunde hinzufügen"
            placeholder="Firmenname suchen …"
            value={selected}
            minChars={0}
            loadOptions={loadOptions}
            onChange={setSelected}
            emptyText="Kein Kunde gefunden"
          />
        </div>
        <Button
          type="button"
          onClick={() => void handleAssign()}
          loading={saving}
          disabled={!selected}
        >
          Hinzufügen
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lädt …
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-label-secondary">Noch keine Kunden zugewiesen.</p>
      ) : (
        <ul className="divide-y divide-separator rounded-md border border-separator">
          {items.map((gp) => (
            <li
              key={gp.id}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{gp.firmenname}</div>
                <div className="truncate text-xs text-label-secondary">
                  {[
                    gp.kundennummer ? `Nr. ${gp.kundennummer}` : null,
                    gp.ort || null,
                    gp.hero_contact_id != null ? `Hero #${gp.hero_contact_id}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="danger"
                disabled={saving}
                onClick={() => void handleUnassign(gp)}
              >
                Entfernen
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
